/**
 * SuccessModal Component
 *
 * Displays a success modal after ritual creation.
 * Shows confirmation message with action buttons.
 */

import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader } from "@/components/ui/dialog"

interface SuccessModalProps {
  isOpen: boolean
  onViewRitual: () => void
  onBackToRituality?: () => void
  ritualId?: string
}

export function SuccessModal({
  isOpen,
  onViewRitual,
  onBackToRituality,
  ritualId,
}: SuccessModalProps) {
  const handleShareRitual = async () => {
    if (!ritualId) return

    const ritualUrl = `${window.location.origin}/rituals/${ritualId}`

    try {
      await navigator.clipboard.writeText(ritualUrl)
      toast.success("Link copied successfully!")

      // Navigate to the ritual detail page after copying
      window.location.href = `/rituals/${ritualId}`
    } catch {
      toast.error("Failed to copy link")
    }
  }

  const handleBackToRituality = () => {
    window.location.href = "/"
  }

  return (
    <Dialog open={isOpen}>
      <DialogContent showCloseButton={false} className="sm:max-w-lg text-center p-8">
        <DialogHeader className="flex flex-col items-center">
          {/* Title */}
          <h2 className="text-2xl font-bold text-white uppercase mt-2">Your Ritual Is Live!</h2>

          {/* Description */}
          <DialogDescription className="text-primary mt-2 text-center">
            <p>Your ritual has been published successfully.</p>
            <p>Others can now discover, join, and experience it with you.</p>
          </DialogDescription>
        </DialogHeader>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 mt-6">
          {/* Primary Button - View Ritual */}
          <Button onClick={onViewRitual} className="w-full bg-white font-semibold">
            View Ritual
          </Button>

          {/* Secondary Button - Share Ritual */}
          {ritualId && (
            <Button
              onClick={handleShareRitual}
              variant="outline"
              className="w-full border-white text-white"
            >
              Share Ritual
            </Button>
          )}

          {/* Tertiary Button - Back to My Rituality */}
          {onBackToRituality && (
            <Button
              onClick={handleBackToRituality}
              variant="outline"
              className="w-full border-white text-white"
            >
              Back to My Rituality
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
